import type { DataSession } from "../../../../data/core/session";
import type { RecommendedSongs } from "@hooks/trpc/useRecommendedSongs";

export type ForYouFetchParams = {
  limit?: number;
  lastFetchedSongId?: string;
  lastViewedSongId?: string;
  /**
   * If true, invalidate the cache before fetching.
   * Used when auth changes to ensure we don't get cached public/old user data.
   */
  skipCache?: boolean;
  /**
   * Read-only polling mode. Used while import onboarding is waiting for the
   * import-specific recommendation row; the import flow owns generation.
   */
  suppressGeneration?: boolean;
  /**
   * Only return the import-specific onboarding recommendation row. Used by
   * welcome_import_pending so generic cold-start/vector rows cannot satisfy
   * the first-song prompt.
   */
  importOnly?: boolean;
};

export function createForYouApi(data: DataSession) {
  const trpcUtils = data.utils;
  async function fetchForYouRecommendedSongs(
    params: ForYouFetchParams,
  ): Promise<RecommendedSongs> {
    // When auth changes, invalidate the cache to ensure we don't get stale data
    // from a different user (public vs authenticated, or different users)
    if (params.skipCache) {
      console.audio.log("fetchForYouRecommendedSongs: invalidating cache");
      await trpcUtils.catalog.songs.listRecommendedSongs.invalidate();
    }

    console.audio.log("fetchForYouRecommendedSongs", {
      limit: params.limit,
      lastFetchedSongId: params.lastFetchedSongId,
      lastViewedSongId: params.lastViewedSongId,
      suppressGeneration: params.suppressGeneration,
      importOnly: params.importOnly,
    });

    const result = await trpcUtils.catalog.songs.listRecommendedSongs.fetch({
      limit: params.limit,
      lastFetchedSongId: params.lastFetchedSongId,
      lastViewedSongId: params.lastViewedSongId,
      suppressGeneration: params.suppressGeneration,
      importOnly: params.importOnly,
    });

    console.audio.log("fetchForYouRecommendedSongs: fetched", {
      count: result.length,
    });
    return result;
  }

  return { fetchForYouRecommendedSongs };
}
